import { Component, computed, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { PaymentProvider } from '../../../core/models/entities/vendor';
import { SharedModule } from '../../../shared/shared.module';
import { SharedManagerModule } from '../../../shared-manager/shared-manager.module';
import { AdminTabsComponent } from '../../components/admin-tabs.component';
import { VendorContactSettingsComponent } from '../../components/vendor-contact-settings/vendor-contact-settings.component';
import { VendorBillingSettingsComponent } from '../../components/vendor-billing-settings/vendor-billing-settings.component';
import { VendorLogoSettingsComponent } from '../../components/vendor-logo-settings/vendor-logo-settings.component';
import { VendorTermsSettingsComponent } from '../../components/vendor-terms-settings/vendor-terms-settings.component';
import { VendorUrlSettingsComponent } from '../../components/vendor-url-settings/vendor-url-settings.component';
import { SavableBeforeExit } from '../../../rooms/interfaces/savable-before-exit';

@Component({
  selector: 'app-vendor-settings-page',
  template: `
    <app-page-header
      title="Organisation"
      [breadcrumbs]="breadcrumbs()"
    ></app-page-header>

    <app-spacer></app-spacer>

    @if (vendor()?.payment_provider === PaymentProvider.Payrexx) {
      <app-admin-tabs></app-admin-tabs>
      <app-spacer size="6"></app-spacer>
    }

    <div class="flex flex-col space-y-6">
      <!-- Coordonnées -->
      <app-vendor-contact-settings
        [authUser]="authUser()"
      ></app-vendor-contact-settings>

      <!-- Informations de facturation -->
      <app-vendor-billing-settings
        [authUser]="authUser()"
      ></app-vendor-billing-settings>

      <!-- Logo -->
      <app-vendor-logo-settings
        [authUser]="authUser()"
      ></app-vendor-logo-settings>

      <!-- Conditions générales -->
      <app-vendor-terms-settings
        [authUser]="authUser()"
      ></app-vendor-terms-settings>

      <!-- Lien de partage -->
      <app-vendor-url-settings
        [authUser]="authUser()"
      ></app-vendor-url-settings>
    </div>
  `,
  imports: [
    SharedModule,
    SharedManagerModule,
    AdminTabsComponent,
    VendorContactSettingsComponent,
    VendorBillingSettingsComponent,
    VendorLogoSettingsComponent,
    VendorTermsSettingsComponent,
    VendorUrlSettingsComponent,
  ],
})
export class VendorSettingsPageComponent implements SavableBeforeExit {
  @ViewChild(VendorContactSettingsComponent)
  contactSettings: VendorContactSettingsComponent;
  @ViewChild(VendorBillingSettingsComponent)
  billingSettings: VendorBillingSettingsComponent;
  @ViewChild(VendorLogoSettingsComponent)
  logoSettings: VendorLogoSettingsComponent;
  @ViewChild(VendorTermsSettingsComponent)
  termsSettings: VendorTermsSettingsComponent;
  @ViewChild(VendorUrlSettingsComponent)
  urlSettings: VendorUrlSettingsComponent;

  authUser = this.store.selectSignal(fromStore.selectAuthUser);
  vendor = this.store.selectSignal(fromStore.selectAuthVendor);

  protected readonly PaymentProvider = PaymentProvider;

  breadcrumbs = computed(() => {
    if (this.vendor()?.payment_provider === PaymentProvider.Payrexx) {
      return [
        { label: 'Administration', routerLink: '/administration' },
        {
          label: 'Données administratives',
          routerLink: '/administration',
        },
      ];
    } else {
      return undefined;
    }
  });

  constructor(private store: Store) {}

  /**
   * Sauvegarde tous les sous-composants qui ont des modifications
   */
  async saveChanges(): Promise<boolean> {
    const componentsToSave = [];

    // Vérifier chaque composant pour voir s'il a des changements
    if (this.contactSettings?.form?.dirty) {
      componentsToSave.push(this.contactSettings.save());
    }
    if (this.billingSettings?.form?.dirty) {
      componentsToSave.push(this.billingSettings.save());
    }
    if (this.logoSettings?.hasChanges()) {
      componentsToSave.push(this.logoSettings.save());
    }
    if (this.termsSettings?.hasChanges()) {
      componentsToSave.push(this.termsSettings.save());
    }
    if (this.urlSettings?.form?.dirty) {
      componentsToSave.push(this.urlSettings.save());
    }

    // S'il n'y a rien à sauvegarder, retourner true
    if (componentsToSave.length === 0) {
      return true;
    }

    // Sauvegarder tous les composants en parallèle
    const results = await Promise.all(componentsToSave);

    // Retourner true seulement si tous ont réussi
    return results.every((result) => result === true);
  }
}
